import { FormikHelpers, Formik, Form } from "formik";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { useTenant } from "src/components/LoggedInAdminProvider";
import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import FormikRadioField, {
  RadioOptions
} from "src/components/form/FormikRadioField";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import FormikTextField from "src/components/form/FormikTextField";
import { EDIT_CAMPAIGN_CELLER_MESSAGING } from "src/graphql/campaignEditCallerMessaging";
import {
  campaignEditCallerMessaging,
  campaignEditCallerMessagingVariables
} from "src/graphql/generated/campaignEditCallerMessaging";
import {
  getCampaign_campaign,
  getCampaign_campaign_customCallerAlert,
  getCampaign_campaign_customEndCard
} from "src/graphql/generated/getCampaign";
import useMutation from "src/util/useMutation";

import { useCampaignEditEvents } from "./CampaignEditorsProvider";
import { FormikSaveButtons } from "./SaveButtons";

enum CustomAlertBarOptions {
  NO_EXPIRATION = "customAlertBar_noExpiration",
  EXPIRATION = "customAlertBar_withExpiration"
}

type Values = {
  customAlertBar: boolean;
  customCampaignComplete: boolean;
  customCampaignPaused: boolean;
  customCampaignOutOfHours: boolean;
  customAlertBarOptions: CustomAlertBarOptions;
  customAlertBarMessage: string | undefined;
  customCampaignCompleteMsg: string | undefined;
  customCampaignPausedMsg: string | undefined;
  customCampaignOutOfHoursMsg: string | undefined;
  expirationDate: string | undefined;
  expirationTime: string | undefined;
  goToNext?: boolean;
};

const useStyles = makeStyles(() => ({
  subOptionWrapper: {
    paddingLeft: "46px"
  },
  durationRadioWrapper: {
    paddingTop: "16px",
    paddingBottom: "16px"
  },
  timePickerWrapper: {
    gap: "1rem",
    display: "flex",
    alignItems: "baseline"
  },
  alert: {
    marginBottom: "1rem"
  }
}));

function ExpirationDatePicker({ timezone }: { timezone: string }): JSX.Element {
  const tenant = useTenant()!;
  const classes = useStyles();

  const timezoneString = tenant.timezoneOptions.find(
    tz => tz.timezone === timezone
  )?.displayName;

  return (
    <div className={classes.timePickerWrapper}>
      <FormikTextField
        label="Expiration Date"
        type="date"
        name={"expirationDate"}
      />
      <FormikTextField
        label="Expiration Time"
        type="time"
        name={"expirationTime"}
      />
      <span>{timezoneString}</span>
    </div>
  );
}

function MultilineInputWithWrapper({
  label,
  name,
  isSubmitting,
  currentMessage
}: {
  label: string;
  name: string;
  isSubmitting: boolean;
  currentMessage: string;
}): JSX.Element {
  const classes = useStyles();

  return (
    <div className={classes.subOptionWrapper}>
      <Alert severity="info" icon={false} className={classes.alert}>
        <strong>Current Message:</strong> {currentMessage}
      </Alert>
      <FormikTextField
        fullWidth
        multiline
        minRows={3}
        label={label}
        name={name}
        disabled={isSubmitting}
      />
    </div>
  );
}

function generateInitialFormValue(
  customAlertBarConfig: getCampaign_campaign_customCallerAlert,
  customEndCardConfig: getCampaign_campaign_customEndCard
): Values {
  const defaultDate: DateTime = DateTime.fromISO(
    customAlertBarConfig.expiredAt ||
      DateTime.now().plus({ day: 1 }).toISODate()
  );

  return {
    customAlertBar: customAlertBarConfig.message ? true : false,
    customCampaignComplete: customEndCardConfig.onCompleteMessage
      ? true
      : false,
    customCampaignPaused: customEndCardConfig.onPausedMessage ? true : false,
    customCampaignOutOfHours: customEndCardConfig.onOutOfHoursMessage
      ? true
      : false,
    customAlertBarOptions: customAlertBarConfig.expiredAt
      ? CustomAlertBarOptions.EXPIRATION
      : CustomAlertBarOptions.NO_EXPIRATION,
    customAlertBarMessage: customAlertBarConfig.message || "",
    customCampaignCompleteMsg: customEndCardConfig.onCompleteMessage || "",
    customCampaignPausedMsg: customEndCardConfig.onPausedMessage || "",
    customCampaignOutOfHoursMsg: customEndCardConfig.onOutOfHoursMessage || "",
    expirationDate: defaultDate.toFormat("yyyy-MM-dd"),
    expirationTime: defaultDate.toFormat("HH:mm")
  };
}

export default function CallerMessaging({
  campaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const sendEditEvent = useCampaignEditEvents();

  const validationSchema = yup.object().shape({
    customAlertBar: yup.boolean(),
    customCampaignComplete: yup.boolean(),
    customCampaignPaused: yup.boolean(),
    customCampaignOutOfHours: yup.boolean(),
    customAlertBarMessage: yup.string().when("customAlertBar", {
      is: true,
      then: yup.string().required("Alert bar message reguired")
    }),
    customCampaignCompleteMsg: yup.string().when("customCampaignComplete", {
      is: true,
      then: yup.string().required("Campaign Complete message required")
    }),
    customCampaignPausedMsg: yup.string().when("customCampaignPaused", {
      is: true,
      then: yup.string().required("Campaign Paused message required")
    }),
    customCampaignOutOfHoursMsg: yup.string().when("customCampaignOutOfHours", {
      is: true,
      then: yup.string().required("Campaign Out of Hours message required")
    })
  });

  const alertBarRadio: RadioOptions[] = [
    {
      label: "Never expire",
      value: CustomAlertBarOptions.NO_EXPIRATION
    },
    {
      label: "Expire after a set duration",
      value: CustomAlertBarOptions.EXPIRATION,
      children: (
        <ExpirationDatePicker timezone={campaign.callingHours.timezone} />
      )
    }
  ];

  const [editCampaignCallerMessaging] = useMutation<
    campaignEditCallerMessaging,
    campaignEditCallerMessagingVariables
  >(EDIT_CAMPAIGN_CELLER_MESSAGING, {
    onErrorSnackbarMessage: "Could not edit campaign"
  });

  const handleSave = async (
    {
      customAlertBar,
      customCampaignComplete,
      customCampaignPaused,
      customCampaignOutOfHours,
      customAlertBarOptions,
      expirationDate,
      expirationTime,
      customAlertBarMessage,
      customCampaignCompleteMsg,
      customCampaignPausedMsg,
      customCampaignOutOfHoursMsg,
      goToNext
    }: Values,
    { setSubmitting, resetForm }: FormikHelpers<Values>
  ) => {
    const isoTimestamp = DateTime.fromFormat(
      `${expirationDate} ${expirationTime}`,
      "yyyy-MM-dd HH:mm"
    )
      .setZone(campaign.callingHours.timezone)
      .toISO();

    const { errors, data } = await editCampaignCallerMessaging({
      variables: {
        campaignId: campaign.id,
        customCallerAlert: {
          expiredAt: customAlertBar
            ? customAlertBarOptions === CustomAlertBarOptions.NO_EXPIRATION
              ? undefined
              : isoTimestamp
            : undefined,
          message: customAlertBar ? customAlertBarMessage : undefined
        },
        customEndCard: {
          onCompleteMessage: customCampaignComplete
            ? customCampaignCompleteMsg
            : undefined,
          onPausedMessage: customCampaignPaused
            ? customCampaignPausedMsg
            : undefined,
          onOutOfHoursMessage: customCampaignOutOfHours
            ? customCampaignOutOfHoursMsg
            : undefined
        }
      }
    });

    setSubmitting(false);
    if (!errors) {
      sendEditEvent();
      resetForm({
        values: {
          ...generateInitialFormValue(
            data!.campaignEditCallerMessaging.customCallerAlert,
            data!.campaignEditCallerMessaging.customEndCard
          )
        }
      });
      if (goToNext) {
        goToNextSection();
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  return (
    <>
      <h2>Caller Messaging</h2>
      <Formik
        initialValues={generateInitialFormValue(
          campaign.customCallerAlert,
          campaign.customEndCard
        )}
        onSubmit={handleSave}
        validationSchema={validationSchema}
      >
        {({ dirty, isSubmitting, values }) => (
          <Form>
            <RouteLeavingGuard when={dirty} />
            <h3>Caller Alert Bar</h3>
            <div>
              <div>
                <FormikSwitchField
                  name="customAlertBar"
                  label="Enable customizable alert bar for this campaign"
                  disabled={isSubmitting}
                />
              </div>
              {values.customAlertBar && (
                <div className={classes.subOptionWrapper}>
                  <div className={classes.durationRadioWrapper}>
                    <FormikRadioField
                      label="Alert bar duration"
                      name="customAlertBarOptions"
                      options={alertBarRadio}
                      value={values.customAlertBarOptions}
                    />
                  </div>
                  <FormikTextField
                    fullWidth
                    label="Alert bar message"
                    name="customAlertBarMessage"
                    disabled={isSubmitting}
                  />
                </div>
              )}
            </div>

            <h3>Customizable End Cards</h3>
            <div>
              <FormikSwitchField
                name="customCampaignComplete"
                label='Customize "Campaign Complete" message to callers'
              />
              {values.customCampaignComplete && (
                <MultilineInputWithWrapper
                  label='Custom "Campaign Complete" message'
                  name="customCampaignCompleteMsg"
                  isSubmitting={isSubmitting}
                  currentMessage={campaign.onCompleteMessage}
                />
              )}
            </div>

            <div>
              <FormikSwitchField
                name="customCampaignPaused"
                label='Customize "Campaign Paused" message to callers'
              />
              {values.customCampaignPaused && (
                <MultilineInputWithWrapper
                  label='Custom "Campaign Paused" message'
                  name="customCampaignPausedMsg"
                  isSubmitting={isSubmitting}
                  currentMessage={campaign.onPausedMessage}
                />
              )}
            </div>

            <div>
              <FormikSwitchField
                name="customCampaignOutOfHours"
                label='Customize "Campaign Outside of Dialing Hours" message to callers'
              />
              {values.customCampaignOutOfHours && (
                <MultilineInputWithWrapper
                  label='Customize "Campaign Outside of Dialing Hours" message to callers'
                  name="customCampaignOutOfHoursMsg"
                  isSubmitting={isSubmitting}
                  currentMessage={campaign.outOfHoursMessage}
                />
              )}
            </div>
            <FormikSaveButtons continueWithoutSaving={goToNextSection} />
          </Form>
        )}
      </Formik>
    </>
  );
}
